import { useSuspenseQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { savedListListsQueryOptions } from '@/entities/saved-creators-list';
import { AnimatedSuspense } from '@/shared/components/animated-suspense';
import { Badge } from '@/shared/components/shadcn/badge';
import { Button } from '@/shared/components/shadcn/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/shadcn/collapsible';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';
import { Input } from '@/shared/components/shadcn/input';
import { RadioGroup, RadioGroupItem } from '@/shared/components/shadcn/radio-group';
import type { DiscoverySaveToListResult } from './discovery-save-to-list-dialog-types';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';

export function DiscoverySaveToListDialog({
  close,
  initialTitle,
  isOpen,
  onSave,
}: {
  close: () => void;
  initialTitle: string;
  isOpen: boolean;
  onSave: (result: DiscoverySaveToListResult) => void;
}) {
  const [titleInput, setTitleInput] = useState(initialTitle);
  const [selectedList, setSelectedList] = useState<{
    listId: string;
    title: string;
  } | null>(null);

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save to List</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4">
          <AnimatedSuspense>
            <SavedLists selectedList={selectedList} setSelectedList={setSelectedList} />
          </AnimatedSuspense>
          <div className="flex flex-col gap-2">
            <h2 className="text-foreground text-sm font-medium">Create new list</h2>
            <Input
              placeholder="Type new list title"
              value={titleInput}
              onChange={(event) => setTitleInput(event.target.value)}
              onFocus={() => setSelectedList(null)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button onClick={close} variant="secondary">
            Cancel
          </Button>
          {selectedList ? (
            <Button
              onClick={() =>
                onSave({
                  action: 'save',
                  listId: selectedList.listId,
                  title: selectedList.title,
                })
              }
            >
              Add to selected list
            </Button>
          ) : (
            <Button
              onClick={() =>
                onSave({
                  action: 'create',
                  title: titleInput,
                })
              }
            >
              Create New List
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SavedLists({
  selectedList,
  setSelectedList,
}: {
  selectedList: {
    listId: string;
    title: string;
  } | null;
  setSelectedList: (list: { listId: string; title: string }) => void;
}) {
  const { workspaceId } = useWorkspaceId();
  const { data: lists } = useSuspenseQuery(savedListListsQueryOptions(workspaceId));

  return (
    lists.length > 0 && (
      <Collapsible defaultOpen>
        <CollapsibleTrigger className="text-foreground hover:bg-muted flex w-full items-center justify-between rounded-md px-1 py-1 text-left text-sm font-medium">
          <h2 className="text-foreground text-sm font-medium">Add to existing list</h2>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-xs">{lists.length} lists</span>
            <ChevronDown className="text-muted-foreground size-4" />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">
          <RadioGroup
            value={selectedList?.listId}
            onValueChange={(id) => {
              const list = lists.find((item) => item.id === id);
              if (!list) return;
              setSelectedList({
                listId: id,
                title: list.title,
              });
            }}
            className="max-h-64 gap-1 overflow-auto"
          >
            {lists.map((list, index) => (
              <label
                key={list.id}
                htmlFor={`saved-list-${list.id}`}
                className="border-border flex cursor-pointer items-center gap-3 rounded-md border p-3"
              >
                <RadioGroupItem value={list.id} id={`saved-list-${list.id}`} />
                <div className="flex flex-1 flex-col">
                  <span className="text-sm font-medium">{list.title}</span>
                  <span className="text-muted-foreground text-xs">
                    {list.total_count} creators
                  </span>
                </div>
                {index === 0 && <Badge variant="outline">Recent</Badge>}
              </label>
            ))}
          </RadioGroup>
        </CollapsibleContent>
      </Collapsible>
    )
  );
}
